import { useEffect, useState } from "react";
import { IFormSection, TFormHookParams } from "../../types/common";
import useGet from "../common/useGet";
import {
  API_ACCOUNTS_CREATION_ROLES,
  API_AVAILABLE_LIQUIDATION_BANK_CODES,
} from "../../utils/apis";
import {
  getRegister,
  getResultOptions,
  itemExists,
  useGetUserPermissions,
} from "../../utils/helpers";
import { URL_APPLICATION_USERS } from "../../utils/urls";
import { useGetOwnerDetails } from "../../utils/owner_details";
import {
  CAN_ADD_LOAN_USER,
  CAN_CHANGE_LOAN_USER,
  CAN_EDIT_USERS,
} from "../../utils/constants";
import { useLocationDataForm } from "../location_data/useLocationDataForm";
import useBrn from "../common/useBrn";
import { VALIDATION_PRESETS } from "../../utils/formUtils";

export const useUsersForm = ({
  id,
  form,
  updateFormValues,
  error,
  setError,
  skipRolesApi = false,
}: TFormHookParams & { skipRolesApi?: boolean } = {}) => {
  const isUpdate = id ? true : false;
  const { loadData: getRoles, data: roles } = useGet();
  const {
    loadData: getAvailableLiquidationBankCodes,
    data: availableLiquidationBankCodes,
  } = useGet();

  const {
    // loader: loadBrn,
    brnError: brnError,
    success: brnSuccess,
  } = useBrn(form, updateFormValues);

  const [groups, setGroups] = useState<any>(form?.groups);

  const ownerDetails = useGetOwnerDetails({
    form,
    updateFormValues,
    error,
    setError,
  });

  const location = useLocationDataForm();
  const filteredRoles = roles?.filter(
    (role: any) => role.name !== "Public User",
  );

  const ownerDetailsDisabled = ownerDetails.map((field) => {
    // If the field is already disabled by the success state, keep it that way
    if (field.props?.disabled === true) {
      return field;
    }
    // Otherwise, apply the update mode disabled state
    if (
      [
        "nationality",
        "username",
        "gender",
        "phone_number",
        "nin",
        "email",
      ].includes(field.field_name)
    ) {
      return { ...field, props: { ...field.props, disabled: isUpdate } };
    }
    return field;
  });

  const permissions = useGetUserPermissions();

  useEffect(() => {
    if (
      form?.preferred_option === "TELECOM" &&
      form?.phone_number &&
      !form?.telecom_mobile_number
    ) {
      updateFormValues?.({ telecom_mobile_number: form.phone_number });
    }
  }, [
    form?.phone_number,
    form?.preferred_option,
    form?.telecom_mobile_number,
    updateFormValues,
  ]);

  useEffect(() => {
    if (!skipRolesApi) {
      getRoles({ api: API_ACCOUNTS_CREATION_ROLES });
    }
    setGroups(form?.groups);
  }, [skipRolesApi]);

  useEffect(() => {
    if (setError) {
      setError(brnError);
    }
  }, [brnError]);

  useEffect(() => {
    if (form?.preferred_option === "BANK") {
      getAvailableLiquidationBankCodes({
        api: API_AVAILABLE_LIQUIDATION_BANK_CODES,
      });
    }
  }, [form?.preferred_option]);

  const sections: IFormSection[] = [
    {
      title: "User Details",
      fields: [
        {
          field_name: "role",
          type: "select",
          label: "Account Type",
          description:
            "Select the type of account to assign to this user. This determines their permissions in the system.",
          props: {
            required: true,
            disabled: isUpdate ? true : false,
            value: form?.role?.name,
          },
          options_data: getResultOptions(filteredRoles, "name", "id"),
        },

        {
          field_name: "is_internal_merchant",
          type: "select",
          label: "Is Internal Merchant?",
          description:
            "Select 'Yes' if the merchant belongs to your internal organization. Select 'No' for external merchants.",
          visible: ["Merchant"]?.includes(form?.role_name),
          props: { required: true },
          options_data: [
            { id: true, name: "Yes" },
            { id: false, name: "No" },
          ],
        },
        ...ownerDetailsDisabled,
      ],
    },
    {
      title: "Business Details",
      visible: ["Merchant"]?.includes(form?.role_name),
      fields: [
        {
          field_name: "business_name",
          type: "text",
          label: "Business Name",
          description:
            "Enter the official registered business name or the commonly used trading name.",
          props: { required: true, disabled: brnSuccess },
        },
        {
          field_name: "business_classification",
          type: "select",
          label: "Business Classification",
          description:
            "Choose the legal classification of the business such as company, partnership, or sole proprietor.",
          props: { required: true },
          options_data: [{ id: "COMPANY", name: "Company" }],
        },
        {
          field_name: "industry",
          type: "select",
          label: "Industry",
          description:
            "Select the industry that best describes the business operations.",
          props: { required: true },
          options_data: [{ id: "BANKING", name: "Banking" }],
        },
        {
          field_name: "preferred_option",
          type: "select",
          label: "Preferred Option",
          description:
            "Select whether the business prefers to receive settlements through a Telecom or a Bank.",
          props: { required: true },
          options_data: [
            { id: "TELECOM", name: "Telecom" },
            { id: "BANK", name: "Bank" },
          ],
        },

        {
          field_name: "telecom_mobile_number",
          type: "tel",
          label: "Phone Number",
          visible: ["TELECOM"]?.includes(form?.preferred_option),
          description:
            "Enter the mobile money number that will be used for settlements.",
          props: { required: true, disabled: true },
          validation: VALIDATION_PRESETS.PHONE_UG,
        },

        {
          field_name: "bank_name",
          type: "select",
          label: "Bank Name",
          visible: ["BANK"]?.includes(form?.preferred_option),
          description:
            "Enter the name of the bank where the settlement account is held.",
          props: { required: true },
          options_data: availableLiquidationBankCodes?.map((bank: any) => ({
            id: bank.key,
            name: bank.value,
          })),
        },
        {
          field_name: "account_number",
          type: "number",
          label: "Account Number",
          visible: ["BANK"]?.includes(form?.preferred_option),
          description: "Enter the bank account number for settlement payouts.",
          props: { required: true },
        },
        {
          field_name: "branch_name",
          type: "text",
          label: "Branch Name",
          visible: ["BANK"]?.includes(form?.preferred_option),
          description:
            "Enter the branch name of the bank where the account is registered.",
          props: { required: true },
        },
      ],
    },

    {
      title: "Location Details",
      fields: [...location?.fields(false)],
      visible: ["Merchant", "Agent"].includes(form?.role_name),
    },
    // ROLES

    {
      title: "Account Details",
      visible:
        itemExists(permissions, [
          CAN_EDIT_USERS,
          CAN_CHANGE_LOAN_USER,
          CAN_ADD_LOAN_USER,
        ]) && form?.is_staff,

      fields: [
        {
          field_name: "groups",
          type: "select",
          label: "System Roles",
          description:
            "Assign system roles or permissions groups to this user. These control what they can access.",
          props: { required: true },
          options_data: roles,
          value: groups,
        },
      ],
    },

    // {
    //   title: "Supporting Documents",
    //   visible: ["Merchant", "Agent"]?.includes(form?.role_name),
    //   fields: [
    //     {
    //       field_name: "certificate_of_registration",
    //       visible: ["Merchant"]?.includes(form?.role_name),
    //       type: "file",
    //       label: "Certificate of Registration",
    //       props: { required: true },
    //     },
    //     {
    //       field_name: "tin_certificate",
    //       visible: ["Merchant"]?.includes(form?.role_name),
    //       type: "file",
    //       label: "Tin Certificate",
    //       props: { required: true },
    //     },
    //     {
    //       field_name: "lc1_letter",
    //       visible: ["Agent"]?.includes(form?.role_name),
    //       type: "file",
    //       label: "LC1 Letter",
    //       props: { required: true },
    //     },
    //     {
    //       field_name: "other",
    //       visible: ["Merchant", "Agent"]?.includes(form?.role_name),
    //       type: "file",
    //       label: "Other supporting documents",
    //       props: { required: false },
    //     },
    //   ],
    // },
    {
      title: "Supporting Documents",
      visible: ["Merchant", "Agent"]?.includes(form?.role_name),
      fields: [
        {
          field_name: "certificate_of_registration",
          type: "file",
          label: "Certificate of Registration",
          description:
            "Upload the official certificate showing the business is legally registered.",
          visible: ["Merchant"]?.includes(form?.role_name),
          props: { required: true },
        },
        {
          field_name: "tin_certificate",
          type: "file",
          label: "TIN Certificate",
          description:
            "Upload the Tax Identification Number certificate issued by the revenue authority.",
          visible: ["Merchant"]?.includes(form?.role_name),
          props: { required: true },
        },
        {
          field_name: "lc1_letter",
          type: "file",
          label: "LC1 Letter",
          description:
            "Upload a signed and stamped LC1 introduction letter confirming the agent’s residence or business location.",
          visible: ["Agent"]?.includes(form?.role_name),
          props: { required: true },
        },
        {
          field_name: "other",
          type: "file",
          label: "Other Supporting Documents",
          description:
            "Upload any additional documents required for verification (optional).",
          visible: ["Merchant", "Agent"]?.includes(form?.role_name),
          props: { required: false },
        },
      ],
    },
  ];

  const title = "Register User";
  const redirect = getRegister(URL_APPLICATION_USERS);

  // const getConfigOptions = () => {
  // const { loadData: getRoles, data: roles } = useGet();
  // useGet();
  // useEffect(() => {
  //   getRoles({
  //     api: API_ACCOUNTS_ROLES,
  //     params: {},
  //   });
  // }, []);
  // return {
  //   roles: getResultOptions(roles, "name", "id"),
  // };
  // };

  const options = {
    // ...getConfigOptions(),
  };
  return { sections, title, redirect, options };
};
